import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './EducationDashboard.css';

const EducationDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State for applications
  const [applications, setApplications] = useState([]);
  const [appLoading, setAppLoading] = useState(true);
  const [appError, setAppError] = useState('');
  
  // State for education programs
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programsError, setProgramsError] = useState('');

  // Redirect if user doesn't have education access
  useEffect(() => {
    if (currentUser && !currentUser.education) {
      navigate('/staff-dashboard');
    }
  }, [currentUser, navigate]);

  // Fetch submitted applications for education grants
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setAppLoading(true);
        const response = await api.get('/api/health-program-applications?portal=education');
        setApplications(response.data.applications || []);
        setAppLoading(false);
      } catch (err) {
        setAppError(err.response?.data?.message || 'Error fetching applications');
        setAppLoading(false);
      }
    };
    
    const fetchPrograms = async () => {
      try {
        setProgramsLoading(true);
        const response = await api.get('/api/health-programs?category=education');
        setPrograms(response.data.programs || []);
        setProgramsLoading(false);
      } catch (err) {
        setProgramsError(err.response?.data?.message || 'Error fetching programs');
        setProgramsLoading(false);
      }
    };
    
    fetchApplications();
    fetchPrograms();
  }, []);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Helper function to display the right badge class based on status
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'status-badge status-draft';
      case 'pendingapproval': return 'status-badge status-pending';
      case 'published': return 'status-badge status-published';
      case 'closed': return 'status-badge status-closed';
      case 'submitted': return 'status-badge status-submitted';
      case 'reviewed': return 'status-badge status-reviewed';
      case 'approved': return 'status-badge status-approved';
      case 'rejected': return 'status-badge status-rejected';
      case 'needsmoreinfo': return 'status-badge status-needsmore';
      default: return 'status-badge';
    }
  };

  return (
    <div className="education-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1>Education Portal</h1>
        <div className="header-actions">
          <Link to="/grants?category=education" className="primary-button">
            <span className="button-icon">📊</span>
            Programs
          </Link>
          <Link to="/education-applications" className="primary-button">
            <span className="button-icon">📝</span>
            Applications
          </Link>
          <Link to="/file-manager" className="primary-button">
            <span className="button-icon">📁</span>
            File Manager
          </Link>
          <Link to="/applicant-scores" className="primary-button">
            <span className="button-icon">🏆</span>
            Applicant Scores
          </Link>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <h3>Active Programs</h3>
            <p className="stat-value">
              {programsLoading ? '...' : programs.filter(p => p.status === 'active').length}
            </p>
          </div>
          <div className="stat-icon">📊</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-content">
            <h3>Pending Applications</h3>
            <p className="stat-value">
              {appLoading ? '...' : applications.filter(a => a.status === 'submitted').length}
            </p>
          </div>
          <div className="stat-icon">📝</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-content">
            <h3>Awarded Grants</h3>
            <p className="stat-value">
              {appLoading ? '...' : applications.filter(a => a.status === 'approved').length}
            </p>
          </div>
          <div className="stat-icon">🏆</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-content">
            <h3>Total Applications</h3>
            <p className="stat-value">{appLoading ? '...' : applications.length}</p>
          </div>
          <div className="stat-icon">📋</div>
        </div>
      </div>
      
      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Grants Card */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Education Programs</h2>
            <Link to="/grants?category=education" className="view-all-link">View All</Link>
          </div>
          
          {programsLoading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Loading programs...</p>
            </div>
          ) : programsError ? (
            <div className="error-message">{programsError}</div>
          ) : programs.length === 0 ? (
            <div className="empty-state">
              <p>No education programs available. Create one to get started.</p>
              <Link to="/create-grant" className="action-button">Create Program</Link>
            </div>
          ) : (
            <div className="grants-list">
              {programs.slice(0, 3).map(program => (
                <div key={program._id} className="grant-item">
                  <h3>{program.title || 'Untitled'}</h3>
                  <div className="grant-meta">
                    <span className={`grant-type grant-type-${String(program.programType || 'default').toLowerCase()}`}>
                      {program.programType || 'Other'}
                    </span>
                    <span className={getStatusBadgeClass(program.status || 'draft')}>
                      {program.status || 'Draft'}
                    </span>
                  </div>
                  <p className="grant-description">
                    {program.description ? (() => {
                      const tmp = document.createElement('div');
                      tmp.innerHTML = program.description;
                      const plain = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
                      return plain.length > 100 ? plain.substring(0, 100) + '...' : plain;
                    })() : 'No description'}
                  </p>
                  {program.applicationDeadline && (
                    <div className="grant-dates">
                      <span><span className="date-label">Deadline:</span> {formatDate(program.applicationDeadline)}</span>
                    </div>
                  )}
                  <div className="grant-actions">
                    <Link to={`/health-programs?category=education&program=${program._id}`} className="action-button">
                      View Details
                    </Link>
                    <Link to={`/edit-education-program/${program._id}`} className="action-button secondary">
                      Edit
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Applications Card */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Recent Applications</h2>
            <Link to="/education-applications" className="view-all-link">View All</Link>
          </div>
          
          {appLoading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Loading applications...</p>
            </div>
          ) : appError ? (
            <div className="error-message">{appError}</div>
          ) : applications.length === 0 ? (
            <div className="empty-state">
              <p>No applications submitted yet.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="applications-table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Grant</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.slice(0, 5).map(app => (
                    <tr key={app._id}>
                      <td>{app.applicantName}</td>
                      <td>{app.programTitle || app.grantTitle || 'N/A'}</td>
                      <td>{formatDate(app.dateSubmitted)}</td>
                      <td>
                        <span className={getStatusBadgeClass(app.status)}>
                          {app.status || 'Submitted'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="table-action-btn" 
                          onClick={() => navigate(`/admin/applications/${app._id}`)}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Resources Section */}
      <section className="resources-section">
        <h2>Education Resources</h2>
        <div className="resources-grid">
          <div className="resource-card">
            <div className="resource-icon">📚</div>
            <div className="resource-content">
              <h3>Grant Guidelines</h3>
              <p>Learn about the eligibility requirements, deadlines, and application process for education grants.</p>
              <Link to="/guidelines" className="resource-link">View Guidelines</Link>
            </div>
          </div>
          
          <div className="resource-card">
            <div className="resource-icon">🎓</div>
            <div className="resource-content">
              <h3>Scholarship Program</h3>
              <p>Information about available scholarships for RSMIN members and their families.</p>
              <button onClick={() => alert('Coming soon!')} className="resource-link">Learn More</button>
            </div>
          </div>
          
          <div className="resource-card">
            <div className="resource-icon">📋</div>
            <div className="resource-content">
              <h3>FAQ</h3>
              <p>Find answers to frequently asked questions about the education grant process.</p>
              <button onClick={() => alert('Coming soon!')} className="resource-link">View FAQ</button>
            </div>
          </div>
          
          <div className="resource-card">
            <div className="resource-icon">🔍</div>
            <div className="resource-content">
              <h3>Application Review Guide</h3>
              <p>Guidelines and best practices for reviewing grant applications.</p>
              <button onClick={() => alert('Coming soon!')} className="resource-link">View Guide</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationDashboard;